import * as React from 'react'
import { format, addDays } from 'date-fns'
import { CalendarRange, Plus, Pencil, Trash2, Play, X, History, Clock } from 'lucide-react'
import { batchOrderApi, type BatchOrderTemplate, type BatchOrderTemplateInput, type TimeSlot, type BatchOrderRun } from '@/api/batchOrders'
import { driverApi } from '@/api/drivers'
import type { Driver, AIParsedOrder } from '@/types'
import { Button } from '@/components/ui/button'
import BulkOrdersConfirmDrawer from '@/components/orders/BulkOrdersConfirmDrawer'

const WEEKDAY_LABELS = ['一', '二', '三', '四', '五', '六', '日']

const emptyTemplateInput = (): BatchOrderTemplateInput => ({
  name: '',
  order_category: '其他',
  pickup_location: '',
  dropoff_location: '',
  vehicle_type: '',
  passenger_count: 1,
  time_slots: [{ time: '09:00', direction: 'go' }, { time: '17:00', direction: 'return' }],
  weekdays: [2, 4],
  default_weeks: 4,
  payment_method: '現金',
  pricing_template: { total_amount: 0, driver_fee: 0, vendor_rebate: 0 },
  driver_id: undefined,
})

const buildOrderDraft = (template: BatchOrderTemplate, date: Date, slot: TimeSlot): AIParsedOrder & { driver_id?: string } => {
  const [hh, mm] = slot.time.split(':')
  const dt = `${format(date, 'yyyy-MM-dd')}T${hh.padStart(2, '0')}:${mm.padStart(2, '0')}:00`
  const isGo = slot.direction === 'go'
  const pt = template.pricing_template || {}
  return {
    order_category: template.order_category,
    passenger_name: template.name,
    passenger_phone: '',
    passenger_count: template.passenger_count,
    pickup_datetime: dt,
    pickup_location: isGo ? template.pickup_location : template.dropoff_location,
    dropoff_location: isGo ? template.dropoff_location : template.pickup_location,
    is_round_trip: false,
    vehicle_type: template.vehicle_type,
    luggage_count: 0,
    child_seat: 0,
    booster_seat: 0,
    name_board: false,
    pricing: {
      total_amount: pt.total_amount ?? 0,
      driver_amount: 0,
      base_fare: pt.total_amount ?? 0,
      night_surcharge: 0,
      cross_district_fee: 0,
      driver_fee: pt.driver_fee ?? 0,
      vendor_rebate: pt.vendor_rebate ?? 0,
      company_rebate: pt.company_rebate ?? 0,
    },
    payment_method: template.payment_method || '現金',
    driver_id: template.driver_id || undefined,
  }
}

const expandTemplate = (template: BatchOrderTemplate, dateFrom: Date, dateTo: Date) => {
  const drafts: (AIParsedOrder & { driver_id?: string })[] = []
  let cursor = dateFrom
  while (cursor <= dateTo) {
    const isoWeekday = ((cursor.getDay() + 6) % 7) + 1 // 1=一 ... 7=日
    if (template.weekdays.includes(isoWeekday)) {
      template.time_slots.forEach(slot => drafts.push(buildOrderDraft(template, cursor, slot)))
    }
    cursor = addDays(cursor, 1)
  }
  return drafts.sort((a, b) => a.pickup_datetime.localeCompare(b.pickup_datetime))
}

export default function BatchOrdersPage() {
  const [templates, setTemplates] = React.useState<BatchOrderTemplate[]>([])
  const [runs, setRuns] = React.useState<BatchOrderRun[]>([])
  const [drivers, setDrivers] = React.useState<Driver[]>([])
  const [vehicleOptions, setVehicleOptions] = React.useState<string[]>([])
  const [paymentOptions, setPaymentOptions] = React.useState<string[]>([])
  const [loading, setLoading] = React.useState(true)
  const [showHistory, setShowHistory] = React.useState(false)

  const [editingTemplate, setEditingTemplate] = React.useState<BatchOrderTemplate | null>(null)
  const [formData, setFormData] = React.useState<BatchOrderTemplateInput>(emptyTemplateInput())
  const [showForm, setShowForm] = React.useState(false)
  const [submitting, setSubmitting] = React.useState(false)

  const [generatingTemplate, setGeneratingTemplate] = React.useState<BatchOrderTemplate | null>(null)
  const [genDateFrom, setGenDateFrom] = React.useState(format(new Date(), 'yyyy-MM-dd'))
  const [genWeeks, setGenWeeks] = React.useState(4)

  const [drafts, setDrafts] = React.useState<(AIParsedOrder & { driver_id?: string })[]>([])
  const [isBulkOpen, setIsBulkOpen] = React.useState(false)
  const [pendingRun, setPendingRun] = React.useState<{ template_id: string; date_from: string; date_to: string } | null>(null)

  const loadAll = async () => {
    setLoading(true)
    try {
      const [tpls, runHistory, driverRes] = await Promise.all([
        batchOrderApi.listTemplates(),
        batchOrderApi.listRuns(),
        driverApi.getDrivers(1, 100),
      ])
      setTemplates(tpls)
      setRuns(runHistory)
      setDrivers(driverRes.items)
    } finally {
      setLoading(false)
    }
  }

  React.useEffect(() => {
    loadAll()
    const tenantStr = localStorage.getItem('tenant')
    if (tenantStr) {
      try {
        const t = JSON.parse(tenantStr)
        if (t.vehicle_categories?.length) {
          setVehicleOptions(t.vehicle_categories.map((c: any) => typeof c === 'string' ? c : c.name || '').filter(Boolean))
        }
        if (t.payment_methods?.length) {
          setPaymentOptions(t.payment_methods.map((m: any) => typeof m === 'string' ? m : m.name || '').filter(Boolean))
        }
      } catch {}
    }
  }, [])

  const openCreateForm = () => {
    setEditingTemplate(null)
    setFormData(emptyTemplateInput())
    setShowForm(true)
  }

  const openEditForm = (t: BatchOrderTemplate) => {
    setEditingTemplate(t)
    setFormData({
      name: t.name,
      order_category: t.order_category,
      pickup_location: t.pickup_location,
      dropoff_location: t.dropoff_location,
      vehicle_type: t.vehicle_type,
      passenger_count: t.passenger_count,
      time_slots: t.time_slots,
      weekdays: t.weekdays,
      default_weeks: t.default_weeks,
      payment_method: t.payment_method,
      pricing_template: t.pricing_template,
      driver_id: t.driver_id,
    })
    setShowForm(true)
  }

  const handleSaveTemplate = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      if (editingTemplate) {
        await batchOrderApi.updateTemplate(editingTemplate.id, formData)
      } else {
        await batchOrderApi.createTemplate(formData)
      }
      setShowForm(false)
      await loadAll()
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeleteTemplate = async (t: BatchOrderTemplate) => {
    if (!confirm(`確定刪除範本「${t.name}」？`)) return
    await batchOrderApi.deleteTemplate(t.id)
    await loadAll()
  }

  const toggleWeekday = (day: number) => {
    setFormData(prev => ({
      ...prev,
      weekdays: prev.weekdays.includes(day) ? prev.weekdays.filter(d => d !== day) : [...prev.weekdays, day].sort(),
    }))
  }

  const updateTimeSlot = (idx: number, patch: Partial<TimeSlot>) => {
    setFormData(prev => {
      const slots = [...prev.time_slots]
      slots[idx] = { ...slots[idx], ...patch }
      return { ...prev, time_slots: slots }
    })
  }

  const openGenerateModal = (t: BatchOrderTemplate) => {
    setGeneratingTemplate(t)
    setGenDateFrom(format(new Date(), 'yyyy-MM-dd'))
    setGenWeeks(t.default_weeks || 4)
  }

  const handleGenerate = () => {
    if (!generatingTemplate) return
    const dateFrom = new Date(genDateFrom)
    const dateTo = addDays(dateFrom, genWeeks * 7 - 1)
    const generated = expandTemplate(generatingTemplate, dateFrom, dateTo)
    if (generated.length === 0) {
      alert('此規則在所選期間內沒有符合的日期，請確認星期設定')
      return
    }
    setDrafts(generated)
    setPendingRun({
      template_id: generatingTemplate.id,
      date_from: genDateFrom,
      date_to: format(dateTo, 'yyyy-MM-dd'),
    })
    setGeneratingTemplate(null)
    setIsBulkOpen(true)
  }

  const handleBulkSuccess = async (createdOrderIds: string[]) => {
    setIsBulkOpen(false)
    setDrafts([])
    if (pendingRun) {
      try {
        await batchOrderApi.createRun({
          template_id: pendingRun.template_id,
          date_from: pendingRun.date_from,
          date_to: pendingRun.date_to,
          order_ids: createdOrderIds,
        })
      } catch {}
      setPendingRun(null)
    }
    await loadAll()
  }

  const driverName = (id?: string) => drivers.find(d => d.id === id)?.name || '未指定'

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-100 dark:bg-indigo-900/30">
            <CalendarRange className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">批次管理</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowHistory(v => !v)} className="h-9 px-3 text-sm">
            <History className="h-4 w-4 mr-1.5" />
            執行歷史
          </Button>
          <Button onClick={openCreateForm} className="h-9 px-4 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold">
            <Plus className="h-4 w-4 mr-1.5" />
            新增範本
          </Button>
        </div>
      </div>

      {/* 範本清單 */}
      {loading ? (
        <div className="py-16 text-center text-gray-400">載入中...</div>
      ) : templates.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 py-16 text-center text-gray-400">
          尚無批次建單範本，點擊「新增範本」開始設定週期性訂單規則
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates.map(t => (
            <div key={t.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-white">{t.name}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{t.pickup_location} ↔ {t.dropoff_location}</p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEditForm(t)} className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 cursor-pointer">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => handleDeleteTemplate(t)} className="p-1.5 rounded-md text-gray-400 hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-600 cursor-pointer">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap gap-1.5">
                {t.weekdays.map(d => (
                  <span key={d} className="px-2 py-0.5 rounded-full text-xs font-bold bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400">
                    週{WEEKDAY_LABELS[d - 1]}
                  </span>
                ))}
              </div>

              <div className="flex flex-wrap gap-1.5">
                {t.time_slots.map((s, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                    <Clock className="h-3 w-3" />
                    {s.time}（{s.direction === 'go' ? '去' : '回'}）
                  </span>
                ))}
              </div>

              <div className="text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
                <p>車型：{t.vehicle_type || '-'}　人數：{t.passenger_count}</p>
                <p>付款：{t.payment_method || '-'}　司機：{driverName(t.driver_id)}</p>
                <p>預設週數：{t.default_weeks} 週</p>
              </div>

              <Button onClick={() => openGenerateModal(t)} className="w-full h-9 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold">
                <Play className="h-4 w-4 mr-1.5" />
                產生訂單
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* 執行歷史 */}
      {showHistory && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
            <span className="font-bold text-gray-900 dark:text-white text-sm">批次建立歷史</span>
          </div>
          {runs.length === 0 ? (
            <div className="py-12 text-center text-gray-400">尚無執行紀錄</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">執行時間</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">範本</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">期間</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">建立筆數</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {runs.map(r => (
                    <tr key={r.id}>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">{format(new Date(r.created_at), 'yyyy-MM-dd HH:mm')}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{templates.find(t => t.id === r.template_id)?.name || '（已刪除範本）'}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{format(new Date(r.date_from), 'MM/dd')} ～ {format(new Date(r.date_to), 'MM/dd')}</td>
                      <td className="px-4 py-3 text-right font-bold text-indigo-600 dark:text-indigo-400">{r.order_count} 筆</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* 範本新增/編輯 Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <form onSubmit={handleSaveTemplate} className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between sticky top-0 bg-white dark:bg-gray-800">
              <h2 className="font-bold text-gray-900 dark:text-white">{editingTemplate ? '編輯範本' : '新增範本'}</h2>
              <button type="button" onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">範本名稱</label>
                <input required type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })}
                  placeholder="例：台積-六家高鐵 接送合約"
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">上車地點</label>
                  <input required type="text" value={formData.pickup_location} onChange={e => setFormData({ ...formData, pickup_location: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">下車地點</label>
                  <input required type="text" value={formData.dropoff_location} onChange={e => setFormData({ ...formData, dropoff_location: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">車型</label>
                  <select value={formData.vehicle_type} onChange={e => setFormData({ ...formData, vehicle_type: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white cursor-pointer">
                    <option value="">選擇車型</option>
                    {vehicleOptions.map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">人數</label>
                  <input type="number" min={1} value={formData.passenger_count} onChange={e => setFormData({ ...formData, passenger_count: parseInt(e.target.value) || 1 })}
                    className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">重複星期</label>
                <div className="flex gap-1.5">
                  {WEEKDAY_LABELS.map((label, i) => {
                    const day = i + 1
                    const active = formData.weekdays.includes(day)
                    return (
                      <button key={day} type="button" onClick={() => toggleWeekday(day)}
                        className={`h-9 w-9 rounded-lg text-sm font-bold transition-colors cursor-pointer ${active ? 'bg-indigo-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
                        {label}
                      </button>
                    )
                  })}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">時段（去程/回程）</label>
                  <button type="button" onClick={() => setFormData(prev => ({ ...prev, time_slots: [...prev.time_slots, { time: '09:00', direction: 'go' }] }))}
                    className="text-xs text-indigo-600 hover:text-indigo-700 font-bold cursor-pointer">+ 新增時段</button>
                </div>
                <div className="space-y-2">
                  {formData.time_slots.map((slot, idx) => (
                    <div key={idx} className="flex gap-2">
                      <input type="time" value={slot.time} onChange={e => updateTimeSlot(idx, { time: e.target.value })}
                        className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
                      <select value={slot.direction} onChange={e => updateTimeSlot(idx, { direction: e.target.value as 'go' | 'return' })}
                        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white cursor-pointer">
                        <option value="go">去程</option>
                        <option value="return">回程</option>
                      </select>
                      <button type="button" onClick={() => setFormData(prev => ({ ...prev, time_slots: prev.time_slots.filter((_, i) => i !== idx) }))}
                        className="px-3 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 text-xs cursor-pointer">移除</button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">付款方式</label>
                  <select value={formData.payment_method || ''} onChange={e => setFormData({ ...formData, payment_method: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white cursor-pointer">
                    {paymentOptions.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">預設指派司機</label>
                  <select value={formData.driver_id || ''} onChange={e => setFormData({ ...formData, driver_id: e.target.value || undefined })}
                    className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white cursor-pointer">
                    <option value="">不指派（待派車）</option>
                    {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">車資</label>
                  <input type="number" value={formData.pricing_template?.total_amount ?? 0}
                    onChange={e => setFormData({ ...formData, pricing_template: { ...formData.pricing_template, total_amount: parseInt(e.target.value) || 0 } })}
                    className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">司機費用</label>
                  <input type="number" value={formData.pricing_template?.driver_fee ?? 0}
                    onChange={e => setFormData({ ...formData, pricing_template: { ...formData.pricing_template, driver_fee: parseInt(e.target.value) || 0 } })}
                    className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">廠商回金</label>
                  <input type="number" value={formData.pricing_template?.vendor_rebate ?? 0}
                    onChange={e => setFormData({ ...formData, pricing_template: { ...formData.pricing_template, vendor_rebate: parseInt(e.target.value) || 0 } })}
                    className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">預設重複週數</label>
                <input type="number" min={1} value={formData.default_weeks} onChange={e => setFormData({ ...formData, default_weeks: parseInt(e.target.value) || 1 })}
                  className="mt-1 w-32 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
              </div>
            </div>
            <div className="p-4 border-t border-gray-100 dark:border-gray-700 flex justify-end gap-2 sticky bottom-0 bg-white dark:bg-gray-800">
              <Button type="button" variant="ghost" onClick={() => setShowForm(false)} className="h-9 px-4 text-sm">取消</Button>
              <Button type="submit" disabled={submitting} className="h-9 px-4 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold">
                {submitting ? '儲存中...' : '儲存範本'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* 產生訂單 Modal */}
      {generatingTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm">
            <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <h2 className="font-bold text-gray-900 dark:text-white">產生訂單 - {generatingTemplate.name}</h2>
              <button onClick={() => setGeneratingTemplate(null)} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">起始日期</label>
                <input type="date" value={genDateFrom} onChange={e => setGenDateFrom(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">重複週數</label>
                <input type="number" min={1} value={genWeeks} onChange={e => setGenWeeks(parseInt(e.target.value) || 1)}
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                將展開 {format(new Date(genDateFrom), 'yyyy-MM-dd')} ～ {format(addDays(new Date(genDateFrom), genWeeks * 7 - 1), 'yyyy-MM-dd')} 期間內符合星期設定的訂單，產生前可逐筆檢視/編輯/刪除。
              </p>
            </div>
            <div className="p-4 border-t border-gray-100 dark:border-gray-700 flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setGeneratingTemplate(null)} className="h-9 px-4 text-sm">取消</Button>
              <Button type="button" onClick={handleGenerate} className="h-9 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold">
                預覽並產生
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 批次確認清單（沿用既有元件） */}
      <BulkOrdersConfirmDrawer
        isOpen={isBulkOpen}
        onClose={() => { setIsBulkOpen(false); setDrafts([]); setPendingRun(null) }}
        orders={drafts}
        onSuccess={handleBulkSuccess}
      />
    </div>
  )
}
